import type JSZip from "jszip";
import { DOMParser as XMLDOMParser } from "@xmldom/xmldom";
import type { Revision } from "~/types/Revision";

export type ImportedRevisionHeaders = NonNullable<Revision["headers"]>;

type HeaderReference = {
  type: "default" | "first" | "even" | string;
  relationshipId: string;
};

type HeaderRelationship = {
  id: string;
  target: string;
};

type ParsedHeaderParagraph = {
  alignment?: "left" | "center" | "right";
  text: string;
  includePageNumbers: boolean;
};

const HEADER_TYPE_PRIORITY = ["default", "first", "even"];

const normalizeWordXml = (value: string) => {
  return value
    .replace(/\s+xmlns(:[A-Za-z0-9_-]+)?="[^"]*"/g, "")
    .replace(/(<\/?)([A-Za-z0-9_-]+:)([A-Za-z0-9_-]+)/g, "$1$3")
    .replace(/(\s)([A-Za-z0-9_-]+:)([A-Za-z0-9_-]+)=/g, "$1$3=");
};

const normalizeTagName = (tagName: string) => {
  return tagName.split(":").pop()?.toLowerCase() ?? tagName.toLowerCase();
};

const getElements = (parent: ParentNode, tagName: string): Element[] => {
  const normalizedTagName = normalizeTagName(tagName);
  const searchableParent = parent as ParentNode & {
    getElementsByTagName?: (name: string) => HTMLCollectionOf<Element>;
  };

  return Array.from(searchableParent.getElementsByTagName?.("*") ?? []).filter(
    (element) => {
      const elementTagName =
        element.tagName?.toLowerCase() ?? element.nodeName?.toLowerCase();
      const elementLocalName = element.localName?.toLowerCase();

      return (
        elementTagName === tagName.toLowerCase() ||
        elementLocalName === normalizedTagName ||
        normalizeTagName(elementTagName ?? "") === normalizedTagName
      );
    }
  );
};

const getFirst = (parent: ParentNode, tagName: string) => {
  return getElements(parent, tagName)[0];
};

const deprefixAttributeName = (attributeName: string) => {
  return attributeName.split(":").pop() ?? attributeName;
};

const getAttr = (element: Element | undefined | null, attributeName: string) => {
  if (!element) return null;

  const candidates = [
    attributeName,
    deprefixAttributeName(attributeName),
    normalizeTagName(attributeName),
  ];

  for (const candidate of candidates) {
    const value = element.getAttribute(candidate);
    if (value != null && value !== "") {
      return value;
    }
  }

  return null;
};

const parseXml = (xml: string) => {
  const parser = new XMLDOMParser();
  return parser.parseFromString(normalizeWordXml(xml), "text/xml");
};

const mapAlignment = (
  alignment?: string | null
): "left" | "center" | "right" | undefined => {
  switch (alignment) {
    case "center":
      return "center";
    case "right":
      return "right";
    case "left":
      return "left";
    default:
      return undefined;
  }
};

const instructionHasPageNumber = (instruction: string) => {
  return /\b(NUM)?PAGES?\b/i.test(instruction);
};

const splitLiteralTrailingPageNumber = (text: string) => {
  const trimmedText = text.trim();

  if (/^\d+$/.test(trimmedText)) {
    return {
      text: "",
      hasPageNumber: true,
    };
  }

  const trailingPageNumberMatch = trimmedText.match(/^(.*?)\s+\d+$/);
  if (!trailingPageNumberMatch) {
    return {
      text: trimmedText,
      hasPageNumber: false,
    };
  }

  return {
    text: trailingPageNumberMatch[1].trim(),
    hasPageNumber: true,
  };
};

const resolveHeaderTarget = (target: string) => {
  if (target.startsWith("/")) {
    return target.replace(/^\//, "");
  }

  if (target.startsWith("word/")) {
    return target;
  }

  return `word/${target.replace(/^\.\//, "")}`;
};

const extractHeaderReferences = (documentXml: string): HeaderReference[] => {
  const document = parseXml(documentXml);

  return getElements(document, "w:headerReference")
    .map((headerReference) => {
      const type = getAttr(headerReference, "w:type") ?? "default";
      const relationshipId =
        getAttr(headerReference, "r:id") ?? getAttr(headerReference, "id");

      if (!relationshipId) return undefined;

      return {
        type,
        relationshipId,
      };
    })
    .filter(Boolean) as HeaderReference[];
};

const extractHeaderRelationships = (relsXml?: string): HeaderRelationship[] => {
  if (!relsXml) return [];

  const rels = parseXml(relsXml);

  return getElements(rels, "Relationship")
    .map((relationship) => {
      const id = getAttr(relationship, "Id");
      const target = getAttr(relationship, "Target");
      const type = getAttr(relationship, "Type");

      if (!id || !target || !type?.includes("/header")) return undefined;

      return {
        id,
        target: resolveHeaderTarget(target),
      };
    })
    .filter(Boolean) as HeaderRelationship[];
};

const sortHeaderReferences = (references: HeaderReference[]) => {
  return [...references].sort((left, right) => {
    const leftIndex = HEADER_TYPE_PRIORITY.indexOf(left.type);
    const rightIndex = HEADER_TYPE_PRIORITY.indexOf(right.type);

    return (
      (leftIndex === -1 ? HEADER_TYPE_PRIORITY.length : leftIndex) -
      (rightIndex === -1 ? HEADER_TYPE_PRIORITY.length : rightIndex)
    );
  });
};

const parseHeaderParagraph = (paragraph: Element): ParsedHeaderParagraph => {
  const paragraphMarkup =
    typeof (paragraph as any).toString === "function"
      ? (paragraph as any).toString()
      : "";
  const pPr = getFirst(paragraph, "w:pPr");
  const alignment = pPr
    ? mapAlignment(getAttr(getFirst(pPr, "w:jc"), "w:val"))
    : undefined;
  const runs = getElements(paragraph, "w:r");

  const textParts: string[] = [];
  let includePageNumbers = false;
  let fieldInstruction = "";
  let isInField = false;
  let isPageNumberField = false;
  let isInFieldResult = false;

  if (instructionHasPageNumber(paragraphMarkup)) {
    includePageNumbers = true;
  }

  for (const simpleField of getElements(paragraph, "w:fldSimple")) {
    if (instructionHasPageNumber(getAttr(simpleField, "w:instr") ?? "")) {
      includePageNumbers = true;
    }
  }

  for (const run of runs) {
    const fieldChar = getFirst(run, "w:fldChar");
    const fieldCharType = getAttr(fieldChar, "w:fldCharType");

    if (fieldCharType === "begin") {
      isInField = true;
      isPageNumberField = false;
      isInFieldResult = false;
      fieldInstruction = "";
      continue;
    }

    if (fieldCharType === "separate") {
      isPageNumberField = instructionHasPageNumber(fieldInstruction);
      includePageNumbers = includePageNumbers || isPageNumberField;
      isInFieldResult = true;
      continue;
    }

    if (fieldCharType === "end") {
      isInField = false;
      isPageNumberField = false;
      isInFieldResult = false;
      fieldInstruction = "";
      continue;
    }

    const instructionText = getFirst(run, "w:instrText")?.textContent ?? "";
    if (instructionText) {
      fieldInstruction += instructionText;
      if (instructionHasPageNumber(instructionText)) {
        includePageNumbers = true;
      }
      continue;
    }

    if (isInField && isPageNumberField && isInFieldResult) {
      continue;
    }

    if (
      run.parentNode &&
      normalizeTagName((run.parentNode as Element).tagName ?? "") ===
        "fldsimple"
    ) {
      const parentInstruction = getAttr(run.parentNode as Element, "w:instr") ?? "";
      if (instructionHasPageNumber(parentInstruction)) {
        continue;
      }
    }

    const tabs = getElements(run, "w:tab").length;
    if (tabs > 0) {
      textParts.push(" ".repeat(tabs));
    }

    for (const textNode of getElements(run, "w:t")) {
      textParts.push(textNode.textContent ?? "");
    }
  }

  const text = textParts.join("").replace(/\s+/g, " ").trim();
  const literalPageNumber = splitLiteralTrailingPageNumber(text);
  if (literalPageNumber.hasPageNumber) {
    includePageNumbers = true;
  }

  return {
    alignment,
    text: literalPageNumber.text,
    includePageNumbers,
  };
};

const parseHeaderXml = (headerXml: string) => {
  const header = parseXml(headerXml);
  const paragraphs = getElements(header, "w:p").map(parseHeaderParagraph);

  return paragraphs.find(
    (paragraph) => paragraph.text || paragraph.includePageNumbers
  );
};

const findFallbackHeaderPaths = (zip: JSZip) => {
  return Object.keys(zip.files)
    .filter((fileName) => /^word\/header\d+\.xml$/i.test(fileName))
    .sort();
};

export const useExtractDocxPageHeaders = async (
  zip: JSZip,
  documentXml: string,
  relsXml?: string
): Promise<ImportedRevisionHeaders | undefined> => {
  const references = sortHeaderReferences(extractHeaderReferences(documentXml));
  const relationships = extractHeaderRelationships(relsXml);
  const paths = references
    .map((reference) => {
      return relationships.find(
        (relationship) => relationship.id === reference.relationshipId
      )?.target;
    })
    .filter(Boolean) as string[];

  const candidatePaths = paths.length > 0 ? paths : findFallbackHeaderPaths(zip);

  for (const path of candidatePaths) {
    const headerXml = await zip.file(path)?.async("string");
    if (!headerXml) continue;

    const parsedHeader = parseHeaderXml(headerXml);
    if (!parsedHeader) continue;

    const header = {
      alignment:
        parsedHeader.alignment ??
        (parsedHeader.includePageNumbers ? "right" : "left"),
      extraText: parsedHeader.text || undefined,
      includePageNumbers: parsedHeader.includePageNumbers,
    };

    if (header.extraText || header.includePageNumbers) {
      return [header];
    }
  }

  return undefined;
};
